import { FC, PropsWithChildren, ReactNode } from 'react'

import { format, parseISO, tz } from '@sequencehq/utils/dates'
import {
  Text,
  BorderRadius6,
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey90,
  Primary13Regular,
  Primary13SemiBold,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'

const ActivityLogItemTemplateComponent: FC<{
  icon: ReactNode
  isLast: boolean
  children: ReactNode
  'data-testid': string
}> = ({ icon, isLast, children, 'data-testid': dataTestId }) => (
  <Flex gap="8px" width="100%" data-testid={dataTestId} role="group">
    <Flex flexDir="column" alignItems="center">
      <Center
        boxSize="24px"
        borderRadius={BorderRadius6}
        backgroundColor={GreyGrey10}
        flexShrink="0"
      >
        {icon}
      </Center>

      {!isLast && (
        <Box
          width="2px"
          height="100%"
          borderLeft={`${GreyGrey30} 2px solid`}
          marginY="2px"
          borderRadius="4px"
        />
      )}
    </Flex>

    {children}
  </Flex>
)

const Body: FC<PropsWithChildren> = ({ children }) => (
  <Flex
    flexDir="column"
    flex="1"
    gap="4px"
    marginTop="4px"
    marginBottom="12px"
    wordBreak="break-word"
  >
    {children}
  </Flex>
)

const Title: FC<
  PropsWithChildren<{ button?: ReactNode; 'data-testid'?: string }>
> = ({ children, button, 'data-testid': dataTestId }) => (
  <Flex
    justifyContent="space-between"
    alignItems="flex-start"
    data-testid={dataTestId ?? 'activity-log.item.title'}
  >
    <Text
      {...Primary13SemiBold}
      color={GreyGrey90}
      whiteSpace="pre-line"
      wordBreak="break-word"
    >
      {children}
    </Text>
    {button && (
      <Box
        flexShrink={0}
        marginTop="-5px"
        marginBottom="-5px"
        visibility="hidden"
        _groupHover={{ visibility: 'visible' }}
      >
        {button}
      </Box>
    )}
  </Flex>
)

const Summary: FC<PropsWithChildren> = ({ children }) => (
  <Text
    {...Primary13Regular}
    color={GreyGrey90}
    whiteSpace="pre-line"
    wordBreak="break-word"
    data-testid="activity-log.item.summary"
  >
    {children}
  </Text>
)

const Metadata: FC<{ email: string | undefined; createdAt: string }> = ({
  email,
  createdAt
}) => (
  <>
    {email && (
      <Text
        {...Primary13Regular}
        color={GreyGrey60}
        data-testid="activity-log.item.metadata.email"
      >
        {email}
      </Text>
    )}
    <Text
      {...Primary13Regular}
      color={GreyGrey60}
      data-testid="activity-log.item.metadata.timestamp"
    >
      {formatTimestamp(createdAt)}
    </Text>
  </>
)

const formatTimestamp = (timestamp: string | Date): string => {
  const parsedTimestamp =
    typeof timestamp === 'string' ? parseISO(timestamp) : timestamp
  return format(parsedTimestamp, 'd MMM yyyy ∙ HH:mm:ss', {
    in: tz('UTC')
  })
}

export const ActivityLogItemTemplate = Object.assign(
  ActivityLogItemTemplateComponent,
  { Body, Metadata, Title, Summary }
)
